import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, Mail, Calendar, ExternalLink, Camera, Play, CheckCircle2, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SignaturePad from "@/components/SignaturePad";
import { api } from "@/lib/api";

const STATUS_STYLES = {
  scheduled: "bg-amber-100 text-amber-700 border-amber-200",
  assigned: "bg-sky/15 text-sky border-sky/30",
  in_progress: "bg-violet-100 text-violet-700 border-violet-200",
  completed: "bg-crop/15 text-crop border-crop/30",
  invoiced: "bg-orange-100 text-orange-700 border-orange-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

export default function PilotJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState({ acres_sprayed: "", chemical_used: "", weather: "", notes: "" });
  const [signature, setSignature] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pilot/work-orders/${id}`);
      setJob(data);
      if (data.report) {
        setReport({
          acres_sprayed: data.report.acres_sprayed ?? "",
          chemical_used: data.report.chemical_used || "",
          weather: data.report.weather || "",
          notes: data.report.notes || "",
        });
      }
    } catch (err) {
      if (err?.response?.status === 401) navigate("/login");
      else toast.error("Could not load job");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const checkIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pilot/work-orders/${id}/check-in`);
      setJob(data);
      toast.success("Checked in. Fly safe!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Check-in failed"); }
    finally { setBusy(false); }
  };

  const checkOut = async () => {
    if (!signature && !job.signature_data_url) {
      if (!window.confirm("Submit without client signature?")) return;
    }
    setBusy(true);
    try {
      const payload = {
        acres_sprayed: report.acres_sprayed === "" ? null : Number(report.acres_sprayed),
        chemical_used: report.chemical_used || null,
        weather: report.weather || null,
        notes: report.notes || null,
        signature_data_url: signature || null,
      };
      const { data } = await api.post(`/pilot/work-orders/${id}/check-out`, payload);
      setJob(data);
      toast.success("Job submitted!");
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.post(`/pilot/work-orders/${id}/photo`, { photo_url: data.url });
      setJob(updated);
      toast.success("Photo added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  if (loading || !job) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading job…</div>;
  }

  const mapsLink = job.location_lat && job.location_lng
    ? `https://www.google.com/maps?q=${job.location_lat},${job.location_lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(job.location_address)}`;
  const directionsLink = `https://www.google.com/maps/dir/?api=1&destination=${job.location_lat && job.location_lng ? `${job.location_lat},${job.location_lng}` : encodeURIComponent(job.location_address)}`;

  const canCheckIn = job.status === "assigned" && !job.check_in_at;
  const canSubmit = job.status === "in_progress";
  const isDone = ["completed", "invoiced", "paid"].includes(job.status);

  return (
    <div data-testid="pilot-job-page" className="min-h-screen bg-muted/30 pb-24">
      <header className="bg-card border-b border-border sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 h-14 flex items-center justify-between">
          <Button asChild variant="ghost" size="sm" className="rounded-full -ml-2">
            <Link to="/pilot"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</Link>
          </Button>
          <Badge className={`border ${STATUS_STYLES[job.status] || "bg-muted"} text-xs`}>{job.status.replace("_", " ")}</Badge>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 space-y-5">
        {/* Client + Job */}
        <section className="bg-card border border-border rounded-3xl shadow-card p-6">
          <h1 className="font-display text-2xl md:text-3xl text-primary font-bold">{job.client_name}</h1>
          {job.farm_name && <div className="text-muted-foreground mt-0.5">{job.farm_name}</div>}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <a href={`tel:${job.client_phone}`} className="inline-flex items-center gap-2 text-primary font-semibold hover:text-accent" data-testid="job-phone">
              <Phone className="h-4 w-4" /> {job.client_phone}
            </a>
            {job.client_email && (
              <a href={`mailto:${job.client_email}`} className="inline-flex items-center gap-2 text-primary hover:text-accent">
                <Mail className="h-4 w-4" /> {job.client_email}
              </a>
            )}
            <span className="inline-flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" /> {job.scheduled_date || "Not scheduled"}
            </span>
            <span className="text-muted-foreground">{job.service}{job.acres ? ` · ${job.acres} acres` : ""} · {job.crop || "—"}</span>
          </div>

          {job.chemical_notes && (
            <div className="mt-4 p-4 rounded-xl bg-muted/40 text-sm">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Job notes</div>
              {job.chemical_notes}
            </div>
          )}
        </section>

        {/* Location */}
        <section className="bg-card border border-border rounded-3xl shadow-card p-6">
          <h2 className="font-display text-lg font-bold text-primary flex items-center gap-2 mb-3">
            <MapPin className="h-5 w-5 text-accent" /> Location
          </h2>
          <div className="text-foreground">{job.location_address}</div>
          {(job.location_lat && job.location_lng) && (
            <div className="text-xs text-muted-foreground mt-1">GPS: {job.location_lat}, {job.location_lng}</div>
          )}
          {job.location_notes && (
            <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
              <strong>Access notes:</strong> {job.location_notes}
            </div>
          )}
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <Button asChild className="rounded-full bg-primary hover:bg-secondary text-primary-foreground flex-1" data-testid="open-directions">
              <a href={directionsLink} target="_blank" rel="noopener noreferrer">
                <MapPin className="h-4 w-4 mr-1.5" /> Get directions
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full flex-1">
              <a href={mapsLink} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-1.5" /> View on map
              </a>
            </Button>
            {job.kml_file_url && (
              <Button asChild variant="outline" className="rounded-full flex-1" data-testid="open-field-map">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${job.kml_file_url}`} target="_blank" rel="noopener noreferrer">
                  <FileText className="h-4 w-4 mr-1.5" /> Field map
                </a>
              </Button>
            )}
          </div>
        </section>

        {/* Action: Check-in */}
        {canCheckIn && (
          <section className="bg-primary text-primary-foreground rounded-3xl p-6 shadow-card">
            <h2 className="font-display text-lg font-bold">Ready to start?</h2>
            <p className="text-sm text-primary-foreground/75 mt-1">Tap below when you arrive at the field.</p>
            <Button onClick={checkIn} disabled={busy} data-testid="check-in-btn" className="mt-4 w-full h-12 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Play className="h-4 w-4 mr-1.5" />} Check in & start job
            </Button>
          </section>
        )}

        {/* Report (visible during in_progress, editable until checkout) */}
        {(canSubmit || isDone) && (
          <section className="bg-card border border-border rounded-3xl shadow-card p-6">
            <h2 className="font-display text-lg font-bold text-primary mb-1">Field report</h2>
            {job.check_in_at && (
              <p className="text-xs text-muted-foreground mb-4">Started {new Date(job.check_in_at).toLocaleString()}</p>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Acres sprayed</Label>
                <Input data-testid="report-acres" type="number" min="0" step="0.5" disabled={isDone}
                  value={report.acres_sprayed} onChange={(e) => setReport({ ...report, acres_sprayed: e.target.value })}
                  className="mt-2 h-11 rounded-xl bg-background" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Weather</Label>
                <Input data-testid="report-weather" disabled={isDone}
                  value={report.weather} onChange={(e) => setReport({ ...report, weather: e.target.value })}
                  placeholder="Clear, 78°F, light NE wind" className="mt-2 h-11 rounded-xl bg-background" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Chemical used</Label>
                <Input data-testid="report-chemical" disabled={isDone}
                  value={report.chemical_used} onChange={(e) => setReport({ ...report, chemical_used: e.target.value })}
                  placeholder="Product name, mix rate, gallons applied" className="mt-2 h-11 rounded-xl bg-background" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Notes</Label>
                <Textarea data-testid="report-notes" disabled={isDone}
                  value={report.notes} onChange={(e) => setReport({ ...report, notes: e.target.value })}
                  rows={3} className="mt-2 rounded-xl bg-background" placeholder="Anything important to record..." />
              </div>
            </div>

            {/* Photos */}
            <div className="mt-6">
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">
                Photos ({job.photo_urls?.length || 0})
              </Label>
              <div className="mt-2 grid grid-cols-3 gap-3">
                {(job.photo_urls || []).map((u, i) => (
                  <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${u}`} target="_blank" rel="noopener noreferrer">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt={`Field ${i + 1}`} className="h-24 w-full object-cover rounded-xl border border-border" />
                  </a>
                ))}
                {!isDone && (
                  <label className="h-24 rounded-xl border-2 border-dashed border-input bg-background hover:bg-muted flex items-center justify-center cursor-pointer text-muted-foreground" data-testid="photo-upload">
                    <Camera className="h-5 w-5" />
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
                  </label>
                )}
              </div>
            </div>

            {/* Signature */}
            <div className="mt-6">
              <Label className="text-xs font-semibold uppercase tracking-wider text-primary/70">Client signature</Label>
              <div className="mt-2">
                {job.signature_data_url ? (
                  <img src={job.signature_data_url} alt="Client signature" className="h-32 bg-background border border-border rounded-xl object-contain w-full" />
                ) : (
                  !isDone && <SignaturePad value={signature} onChange={setSignature} />
                )}
              </div>
            </div>

            {canSubmit && (
              <Button onClick={checkOut} disabled={busy} data-testid="check-out-btn"
                className="mt-6 w-full h-12 rounded-full bg-primary hover:bg-secondary text-primary-foreground font-semibold">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />} Submit & finish job
              </Button>
            )}
            {isDone && (
              <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm">
                <CheckCircle2 className="h-4 w-4 inline mr-1" /> Job completed on {job.check_out_at ? new Date(job.check_out_at).toLocaleString() : "—"}. Thanks!
              </div>
            )}
          </section>
        )}

        {job.status === "scheduled" && !job.assigned_pilot_id && (
          <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm text-center">
            This job hasn't been assigned to a pilot yet. Contact your admin.
          </div>
        )}
      </main>
    </div>
  );
}
